import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { MapDispatchToProps } from 'react-redux';
import { connect } from 'react-redux';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { MonetarySchedule } from 'state/tradeAllocationForm';
import {
  tradeAllocationF2YChanged,
  tradeAllocationNotionalChanged,
  tradeAllocationOfferPriceChanged,
  tradeAllocationSelectedOnBehalfClientChanged,
  tradeAllocationSponsoChanged,
  tradeAllocationSponsoPercentUpFrontChanged,
  tradeAllocationTopUpPercentUpfrontChanged,
  tradeAllocationTradingPriceChanged,
  tradeAllocationUpfrontChanged,
  tradeAllocationXMarginChanged,
} from 'state/tradeAllocationForm/tradeAllocationForm.actions';
import * as tradeAllocationSelector from 'state/tradeAllocationForm/tradeAllocationForm.selector';

interface OwnProps {
  tabId: string;
}

interface StateProps {
  currency?: string;
  notional?: number;
  tradeId?: string;
  xMargin?: number;
  tradingPrice?: number;

  sponso?: number;
  sponsoPercentUpFront?: number;
  f2Y?: number;
  topUpPercentUpFront?: number;
  offerPrice?: number;
  upfrontFee?: number;
  product?: string;
  wrapper?: string;
  fileCode?: string;
  isin?: string;
  selectedOnBehalfClient: ISalesCounterpart | null;
  tradeWay?: string;
  isReadOnly: boolean;
  isLoading: boolean;
  monetarySchedule?: MonetarySchedule;
}

interface DispatchProps {
  onNotionalChange: (newNotional: number | null) => void;
  onXMarginChange: (newXMargin: number | undefined) => void;
  onTradingPriceChange: (newTradingPrice: number | undefined) => void;
  onOfferPriceChange: (newOfferPrice: number | undefined) => void;
  onUpfrontFeeChange: (newUpfront: number | undefined) => void;
  onSponsoChange: (newSponso: number | undefined) => void;
  onSponsoPercentUpFrontChange: (newSponsoPercentUpFront: number | undefined) => void;
  onF2YChange: (newF2Y: number | undefined) => void;
  onTopUpPercentUpFrontChange: (newTopUpPercentUpFront: number | undefined) => void;
  onSelectedOnBehalfClientChange: (newClient: ISalesCounterpart | null) => void;
}
export type TradeAllocationMainFormProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: AppState, { tabId }: OwnProps): StateProps => {
  return {
    currency: tradeAllocationSelector.getCurrency(state, tabId)!,
    notional: tradeAllocationSelector.getNotional(state, tabId)!,
    tradeId: tradeAllocationSelector.getTradeId(state, tabId)!,
    offerPrice: tradeAllocationSelector.getOfferPrice(state, tabId)!,
    tradingPrice: tradeAllocationSelector.getTradingPrice(state, tabId)!,
    upfrontFee: tradeAllocationSelector.getUpfrontFee(state, tabId)!,
    xMargin: tradeAllocationSelector.getXMargin(state, tabId)!,
    product: tradeAllocationSelector.getProduct(state, tabId)!,
    wrapper: tradeAllocationSelector.getWrapper(state, tabId)!,
    fileCode: tradeAllocationSelector.getFileCode(state, tabId)!,
    sponso: tradeAllocationSelector.getSponso(state, tabId),
    sponsoPercentUpFront: tradeAllocationSelector.getSponsoPercentUpFront(state, tabId),
    f2Y: tradeAllocationSelector.getF2Y(state, tabId),
    topUpPercentUpFront: tradeAllocationSelector.getTopUpPercentUpFront(state, tabId),
    isin: tradeAllocationSelector.getIsin(state, tabId)!,
    selectedOnBehalfClient: tradeAllocationSelector.getSelectedOnBehalfClient(state, tabId)!,
    tradeWay: tradeAllocationSelector.getTradeWay(state, tabId),
    isReadOnly: tradeAllocationSelector.isReadonly(state, tabId),
    isLoading: tradeAllocationSelector.isLoading(state, tabId),
    monetarySchedule: tradeAllocationSelector.getMonetarySchedule(state, tabId),
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (dispatch: ThunkDispatch, { tabId }) => ({
  onNotionalChange: (newNotional: number | null) => {
    dispatch(tradeAllocationNotionalChanged(tabId, newNotional));
  },
  onXMarginChange: (newXMargin: number | undefined) => {
    dispatch(tradeAllocationXMarginChanged(tabId, newXMargin));
  },
  onTradingPriceChange: (newTradingPrice: number | undefined) => {
    dispatch(tradeAllocationTradingPriceChanged(tabId, newTradingPrice));
  },
  onOfferPriceChange: (newOfferPrice: number | undefined) => {
    dispatch(tradeAllocationOfferPriceChanged(tabId, newOfferPrice));
  },
  onUpfrontFeeChange: (newUpfront: number | undefined) => {
    dispatch(tradeAllocationUpfrontChanged(tabId, newUpfront));
  },
  onSelectedOnBehalfClientChange: (newClient: ISalesCounterpart | null) => {
    dispatch(tradeAllocationSelectedOnBehalfClientChanged(tabId, newClient));
  },
  onSponsoChange: (newSponso: number | undefined) => {
    dispatch(tradeAllocationSponsoChanged(tabId, newSponso));
  },
  onSponsoPercentUpFrontChange: (newSponsoPercentUpFront: number | undefined) => {
    dispatch(tradeAllocationSponsoPercentUpFrontChanged(tabId, newSponsoPercentUpFront));
  },
  onF2YChange: (newF2Y: number | undefined) => {
    dispatch(tradeAllocationF2YChanged(tabId, newF2Y));
  },
  onTopUpPercentUpFrontChange: (newTopUpPercentUpFront: number | undefined) => {
    dispatch(tradeAllocationTopUpPercentUpfrontChanged(tabId, newTopUpPercentUpFront));
  },
});

export const connectTradeAllocationMainForm = connect<StateProps, DispatchProps, OwnProps, AppState>(
  mapStateToProps,
  mapDispatchToProps,
);
